import { useState, useEffect, useMemo } from 'react';
import {
  useGetAllRuns,
  usePostListProfiles,
  useGetAllSourceTables,
  usePostCancelRun,
} from '@/api/control/control-plane/control-plane';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronLeft, ChevronRight, Settings2, Clock, Loader2, X, Check, CircleMinus, Ban, Copy, ArrowUpRight } from 'lucide-react';
import { RefreshButton } from '@/components/RefreshButton';
import { toast } from 'sonner';
import type {
  RunMetadataDto,
  ProfileDto,
  RunMetadataDtoTriggerSource,
} from '@/api/control/missionImpactAPI.schemas';
import { GetAllRunsRequestStatesItem } from '@/api/control/missionImpactAPI.schemas';
import { RunFilters, type FilterChip } from '@/components/runs/RunFilters';
import { ProfileRunFilters, type ProfileRunFilterChip } from '@/components/profiles/runs/ProfileRunFilters';
import { ErrorDetailsDialog } from '@/components/runs/ErrorDetailsDialog';
import { SkipReasonDialog } from '@/components/runs/SkipReasonDialog';
import { SchemaChangesDialog } from '@/components/runs/SchemaChangesDialog';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Column visibility settings for the run metadata table
 */
export interface ColumnVisibility {
  runId?: boolean;
  profileName?: boolean;
  sourceTableName?: boolean;
  syncMode?: boolean;
  state?: boolean;
  triggerSource?: boolean;
  region?: boolean;
  scheduledUtc?: boolean;
  startedUtc?: boolean;
  completedUtc?: boolean;
  duration?: boolean;
  extractionStartedUtc?: boolean;
  extractionCompletedUtc?: boolean;
  extractionDuration?: boolean;
  loadStartedUtc?: boolean;
  loadCompletedUtc?: boolean;
  loadDuration?: boolean;
  rowsExtracted?: boolean;
  rowsLoaded?: boolean;
  rowsInserted?: boolean;
  rowsUpdated?: boolean;
  rowsDeleted?: boolean;
  schemaChanges?: boolean;
  actions?: boolean;
}

/**
 * Default column visibility configuration
 */
const DEFAULT_COLUMN_VISIBILITY: ColumnVisibility = {
  runId: false,
  profileName: true,
  sourceTableName: true,
  syncMode: false,
  state: true,
  triggerSource: false,
  region: false,
  scheduledUtc: true,
  startedUtc: false,
  completedUtc: false,
  duration: true,
  extractionStartedUtc: false,
  extractionCompletedUtc: false,
  extractionDuration: false,
  loadStartedUtc: false,
  loadCompletedUtc: false,
  loadDuration: false,
  rowsExtracted: true,
  rowsLoaded: true,
  rowsInserted: false,
  rowsUpdated: false,
  rowsDeleted: false,
  schemaChanges: false,
  actions: true,
};

/**
 * Run states that cannot be cancelled (terminal states or already cancelling)
 */
const NON_CANCELLABLE_STATES = ['Completed', 'Failed', 'Cancelled', 'Skipped'];

/**
 * Props for the RunMetadataTable component
 */
export interface RunMetadataTableProps {
  /** Organization ID */
  orgId: string;
  /** Optional profile IDs to filter runs. If not provided, shows all profiles. */
  profileIds?: string[];
  /** Whether to show the profile column (set to false for single-profile views) */
  showProfileColumn?: boolean;
  /** Title for the card */
  title?: string;
  /** Description for the card */
  description?: string;
  /** Storage key for column visibility preferences */
  storageKey?: string;
  /**
   * When true, the table fills its parent's available height and scrolls internally (both axes),
   * keeping the column header, horizontal scrollbar, and pagination footer in view without
   * page-scrolling. The parent must provide a bounded flex-column height. Defaults to false so
   * embedded usages keep their natural height.
   */
  fillHeight?: boolean;
}

/**
 * Gets the appropriate badge styling, icon, label, and tooltip for a run state
 */
function getStateDisplay(state: string | undefined, runStates: Record<string, string>): {
  className: string;
  icon: React.ReactNode;
  label: string;
  tooltip: string;
} {
  const label = state ? (runStates[state] || state) : (runStates.Unknown || 'Unknown');
  const tooltipKey = state ? `${state}Tooltip` : 'UnknownTooltip';
  const tooltip = runStates[tooltipKey] ?? label;

  switch (state) {
    case 'Completed':
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium background-success text-success rounded-full',
        icon: <Check className="h-3 w-3" />,
        label,
        tooltip,
      };
    case 'Failed':
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium background-error text-error rounded-full',
        icon: <X className="h-3 w-3" />,
        label,
        tooltip,
      };
    case 'Extracting':
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full',
        icon: <Loader2 className="h-3 w-3 animate-spin" />,
        label,
        tooltip,
      };
    case 'Loading':
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full',
        icon: <Loader2 className="h-3 w-3 animate-spin" />,
        label,
        tooltip,
      };
    case 'WaitingForTargetLease':
    case 'WaitingForSourceCapacity':
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium background-neutral text-neutral rounded-full',
        icon: <Clock className="h-3 w-3" />,
        label,
        tooltip,
      };
    case 'Scheduled':
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium background-neutral text-neutral rounded-full',
        icon: <Clock className="h-3 w-3" />,
        label,
        tooltip,
      };
    case 'Skipped':
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium background-neutral text-neutral rounded-full',
        icon: <CircleMinus className="h-3 w-3" />,
        label,
        tooltip,
      };
    case 'Cancelling':
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium background-warning text-warning rounded-full',
        icon: <Loader2 className="h-3 w-3 animate-spin" />,
        label,
        tooltip,
      };
    case 'Cancelled':
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium background-warning text-warning rounded-full',
        icon: <X className="h-3 w-3" />,
        label,
        tooltip,
      };
    default:
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium background-neutral text-neutral rounded-full',
        icon: null,
        label,
        tooltip,
      };
  }
}

/**
 * Gets the translated label for sync mode
 */
function getSyncModeLabel(syncMode: string | undefined, syncModeLabels: Record<string, string>): string {
  if (!syncMode) return '-';
  return syncModeLabels[syncMode] ?? syncMode;
}

/**
 * Gets the translated label for trigger source
 */
function getTriggerSourceLabel(triggerSource: RunMetadataDtoTriggerSource | undefined, triggerSourceLabels: Record<string, string>): string {
  if (!triggerSource) return '-';
  return triggerSourceLabels[triggerSource as keyof typeof triggerSourceLabels] ?? triggerSource;
}

/**
 * Formats a duration in milliseconds to a human-readable string
 */
function formatDuration(
  startUtc: string | null | undefined,
  completedUtc: string | null | undefined
): string {
  if (!startUtc || !completedUtc) {
    return '-';
  }

  const start = new Date(startUtc).getTime();
  const end = new Date(completedUtc).getTime();
  const durationMs = end - start;

  if (durationMs < 0) {
    return '-';
  }

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${String(hours)}h ${String(minutes % 60)}m`;
  } else if (minutes > 0) {
    return `${String(minutes)}m ${String(seconds % 60)}s`;
  } else {
    return `${String(seconds)}s`;
  }
}

/**
 * Formats a number with thousand separators
 */
function formatNumber(num: number | null | undefined): string {
  if (num === null || num === undefined) {
    return '-';
  }
  return num.toLocaleString();
}

/**
 * Formats bytes to a human-readable string (KB, MB, GB)
 */
function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || bytes === 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);

  return `${value.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

/**
 * Shared run metadata table component with toggleable columns
 */
export function RunMetadataTable({
  orgId,
  profileIds,
  showProfileColumn = true,
  title,
  description,
  storageKey = 'runMetadata.columnVisibility',
  fillHeight = false,
}: RunMetadataTableProps) {
  const { t } = useLanguage();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageTokens, setPageTokens] = useState<Record<number, string | undefined>>({
    0: undefined,
  });
  const [runs, setRuns] = useState<RunMetadataDto[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [selectedError, setSelectedError] = useState<RunMetadataDto | null>(null);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [selectedSkipReason, setSelectedSkipReason] = useState<RunMetadataDto | null>(null);
  const [isSkipReasonDialogOpen, setIsSkipReasonDialogOpen] = useState(false);
  const [selectedSchemaChanges, setSelectedSchemaChanges] = useState<RunMetadataDto | null>(null);
  const [isSchemaChangesDialogOpen, setIsSchemaChangesDialogOpen] = useState(false);

  // Filter state
  const [filters, setFilters] = useState<FilterChip[] | ProfileRunFilterChip[]>([]);
  const [profiles, setProfiles] = useState<ProfileDto[]>([]);
  const [sourceTables, setSourceTables] = useState<{ id: string; name: string }[]>([]);

  // Live update state
  const [isLiveUpdateEnabled, setIsLiveUpdateEnabled] = useState(true);

  // Skipped-run visibility. Skipped runs are hidden by default and revealed via this toggle
  // (mirrors the Live Updates toggle). An explicit Skipped state filter chip still overrides it.
  const [isShowSkippedEnabled, setIsShowSkippedEnabled] = useState(false);

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? { ...DEFAULT_COLUMN_VISIBILITY, ...(JSON.parse(stored) as Partial<ColumnVisibility>) } : DEFAULT_COLUMN_VISIBILITY;
    } catch {
      return DEFAULT_COLUMN_VISIBILITY;
    }
  });

  // Copy state for Run ID
  const [copiedRunId, setCopiedRunId] = useState<string | null>(null);

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(columnVisibility));
    } catch (error) {
      console.error('Failed to save column visibility:', error);
    }
  }, [columnVisibility, storageKey]);

  // Cancel run mutation
  const { mutate: cancelRun, isPending: isCancelling } = usePostCancelRun({
    mutation: {
      onSuccess: () => {
        toast.success(t.allRuns.actions.cancelSuccess);
        void refetch();
      },
      onError: (error) => {
        console.error('Failed to cancel run:', error);
        toast.error(t.allRuns.actions.cancelError);
      },
    },
  });

  // Fetch profiles for filter UI and name lookup
  const { mutate: fetchProfiles } = usePostListProfiles({
    mutation: {
      onSuccess: (response) => {
        setProfiles(response.data.items ?? []);
      },
      onError: (error) => {
        console.error('Failed to load profiles:', error);
      },
    },
  });

  // Create profile name lookup map from profiles
  const profileNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    profiles.forEach((profile) => {
      if (profile.id && profile.name) {
        map[profile.id] = profile.name;
      }
    });
    return map;
  }, [profiles]);

  // Fetch all source tables for filter UI
  const { mutate: fetchSourceTables } = useGetAllSourceTables({
    mutation: {
      onSuccess: (response) => {
        const tables =
          response.data.items?.map((table) => ({
            id: table.id,
            name: table.logicalName,
          })) ?? [];
        setSourceTables(tables);
      },
      onError: (error) => {
        console.error('Failed to load source tables:', error);
      },
    },
  });
 
  // Fetch source tables on mount
  useEffect(() => {
    if (!orgId) return;
    const data: { profileIds?: string[]; onlyEnabled?: boolean } = {};

    // If profileIds are provided, filter source tables by those profiles
    if (profileIds && profileIds.length > 0) {
      data.profileIds = profileIds;
      data.onlyEnabled = true;
    }

    fetchSourceTables({ orgId, data });
  }, [orgId, profileIds, fetchSourceTables]);

  // Fetch profiles on mount (only if showing profile column)
  useEffect(() => {
    if (!orgId || !showProfileColumn) return;
    fetchProfiles({
      orgId,
      data: {
        pageSize: 100, // Get all profiles
      },
    });
  }, [orgId, showProfileColumn, fetchProfiles]);

  // Extract filter values from filter chips (memoized to ensure stable references)
  const { profileFilters, stateFilters, sourceTableFilters } = useMemo(() => ({
    profileFilters: filters.filter((f) => f.type === 'profile').map((f) => f.value),
    stateFilters: filters
      .filter((f) => f.type === 'state')
      .map((f) => f.value as GetAllRunsRequestStatesItem),
    sourceTableFilters: filters.filter((f) => f.type === 'sourceTable').map((f) => f.value),
  }), [filters]);

  // Merge profileIds prop with profile filters (memoized)
  const mergedProfileIds = useMemo(() => {
    const allProfileIds = profileIds ?? [];
    return profileFilters.length > 0
      ? profileFilters
      : (allProfileIds.length > 0 ? allProfileIds : undefined);
  }, [profileFilters, profileIds]);

  // Resolve which run states to request:
  //  - Explicit state filter chips always win (e.g. selecting "Skipped" opts back in).
  //  - Otherwise, Skipped runs are hidden by default: request every state except Skipped.
  //  - When the "Show Skipped runs" toggle is on with no explicit chips, request all states.
  const effectiveStates = useMemo(() => {
    if (stateFilters.length > 0) {
      return stateFilters;
    }
    if (!isShowSkippedEnabled) {
      return Object.values(GetAllRunsRequestStatesItem).filter(
        (state) => state !== GetAllRunsRequestStatesItem.Skipped
      );
    }
    return undefined;
  }, [stateFilters, isShowSkippedEnabled]);

  // Memoize the request object to ensure React Query properly detects changes
  const getAllRunsRequest = useMemo(() => ({
    pageSize: 50,
    continuationToken: pageTokens[currentPage],
    profileIds: mergedProfileIds,
    states: effectiveStates,
    sourceTableLogicalNames: sourceTableFilters.length > 0 ? sourceTableFilters : undefined,
  }), [pageTokens, currentPage, mergedProfileIds, effectiveStates, sourceTableFilters]);

  // Use the query hook with conditional auto-refresh
  const { data, isLoading, isFetching, isRefetching, refetch } = useGetAllRuns(
    orgId,
    getAllRunsRequest,
    {
      query: {
        enabled: !!orgId,
        refetchInterval: isLiveUpdateEnabled ? 5000 : false, // Auto-refresh every 5 seconds when enabled
      },
    }
  );

  const isPending = isLoading;
  const isRefreshing = isFetching && !isLoading && isRefetching;

  // Update runs state when data changes
  useEffect(() => {
    if (data?.data) {
      const items = data.data.items ?? [];
      setRuns(items);
      setHasMore(data.data.hasMore ?? false);

      // Store the continuation token for the next page
      const nextToken = data.data.continuationToken;
      if (nextToken) {
        setPageTokens((prev) => ({ ...prev, [currentPage + 1]: nextToken }));
      }
    }
  }, [data, currentPage]);

  // Reset pagination when filters (or the Skipped visibility toggle) change
  useEffect(() => {
    setCurrentPage(0);
    setPageTokens({ 0: undefined });
  }, [filters, profileIds, isShowSkippedEnabled]);

  // Auto-disable live updates after 5 minutes
  useEffect(() => {
    if (!isLiveUpdateEnabled) return;

    const fiveMinutes = 5 * 60 * 1000;
    const timeout = setTimeout(() => {
      setIsLiveUpdateEnabled(false);
      toast(t.allRuns.liveUpdates.autoDisabledMessage, {
        duration: Infinity,
        action: {
          label: t.allRuns.liveUpdates.reenableButton,
          onClick: () => { setIsLiveUpdateEnabled(true); },
        },
      });
    }, fiveMinutes);

    return () => { clearTimeout(timeout); };
  }, [isLiveUpdateEnabled, t]);

  /**
   * Handles fetching the next page
   */
  const handleNextPage = () => {
    if (!hasMore) return;
    setCurrentPage(currentPage + 1);
  };

  /**
   * Handles fetching the previous page
   */
  const handlePreviousPage = () => {
    if (currentPage === 0) return;
    setCurrentPage(currentPage - 1);
  };

  /**
   * Handles refreshing the current page
   */
  const handleRefresh = () => {
    void refetch();
  };

  /**
   * Handles opening the error details dialog
   */
  const handleShowError = (run: RunMetadataDto) => {
    setSelectedError(run);
    setIsErrorDialogOpen(true);
  };

  /**
   * Handles opening the skip reason dialog
   */
  const handleShowSkipReason = (run: RunMetadataDto) => {
    setSelectedSkipReason(run);
    setIsSkipReasonDialogOpen(true);
  };

  /**
   * Handles row click - opens error or skip reason dialog
   */
  const handleRowClick = (run: RunMetadataDto) => {
    const hasError = run.errorCode && run.state !== 'Skipped';
    const isSkipped = run.state === 'Skipped';

    // Handle error rows (excluding skipped runs)
    if (hasError) {
      handleShowError(run);
    }
    // Handle all skipped runs (with or without skip reasons)
    else if (isSkipped) {
      handleShowSkipReason(run);
    }
  };

  /**
   * Toggles column visibility
   */
  const toggleColumn = (column: keyof ColumnVisibility) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  /**
   * The canonical set of column keys, derived from the default configuration.
   */
  const allColumnKeys = Object.keys(DEFAULT_COLUMN_VISIBILITY) as (keyof ColumnVisibility)[];

  /**
   * Whether every available column is currently visible. Derived from column
   * visibility so it defaults to off, persists with the other column
   * preferences, and automatically un-checks when any single column is hidden.
   */
  const showAllColumns = allColumnKeys.every((key) => columnVisibility[key]);

  /**
   * Turns every column on, or reverts to the default column set.
   */
  const toggleShowAllColumns = () => {
    if (showAllColumns) {
      setColumnVisibility(DEFAULT_COLUMN_VISIBILITY);
    } else {
      const allVisible: ColumnVisibility = { ...columnVisibility };
      for (const key of allColumnKeys) {
        allVisible[key] = true;
      }
      setColumnVisibility(allVisible);
    }
  };

  /**
   * Handles copying a run ID to clipboard
   */
  const handleCopyRunId = (runId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click handler
    navigator.clipboard.writeText(runId).then(
      () => {
        setCopiedRunId(runId);
        toast.success(t.allRuns.actions.copiedToClipboard);
        setTimeout(() => { setCopiedRunId(null); }, 2000);
      },
      () => {
        toast.error(t.allRuns.actions.failedToCopy);
      }
    );
  };

  /**
   * Handles cancelling a run
   */
  const handleCancelRun = (run: RunMetadataDto, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click handler
    if (!run.profileId || !run.sourceTableId || !run.runId) return;

    cancelRun({
      orgId,
      profileId: run.profileId,
      sourceTableId: run.sourceTableId,
      runId: run.runId,
    });
  };

  /**
   * Checks if a run can be cancelled (not in a terminal or cancelling state)
   */
  const canCancelRun = (run: RunMetadataDto): boolean => {
    return !NON_CANCELLABLE_STATES.includes(run.state);
  };

  // Determine which filter component to use
  const FilterComponent = showProfileColumn ? RunFilters : ProfileRunFilters;

  // Layout classes for the fill-height mode (#434): the table body becomes the single internal
  // scroll region (both axes) with a sticky header, so the horizontal scrollbar and pagination
  // footer stay in view. All default to the prior natural-height behavior when fillHeight is false.
  const tableClassName = `[&_tr>*:first-child]:pl-8 [&_tr>*:last-child]:pr-8${
    fillHeight ? ' [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-card' : ''
  }`;
  const tableContainerClassName = fillHeight ? 'flex-1 min-h-0 overflow-y-auto' : undefined;
  const footerClassName = `flex items-center justify-between px-6 py-4 border-t${
    fillHeight ? ' shrink-0' : ''
  }`;

  return (
    <>
      {/* Filters */}
      <div className={`flex items-center gap-2 overflow-x-auto pb-2${fillHeight ? ' shrink-0' : ''}`}>
        <div className="flex-shrink-0">
          <FilterComponent
            profiles={showProfileColumn ? profiles : []}
            sourceTables={sourceTables}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment -- filter types are dynamically chosen based on showProfileColumn
            filters={filters as any}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment -- filter types are dynamically chosen based on showProfileColumn
            onFiltersChange={setFilters as any}
          />
        </div>

        {/* Column Visibility Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="flex-shrink-0">
              <Settings2 className="h-4 w-4 mr-2" />
              {t.allRuns.columnToggle.columnsButton}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>{t.allRuns.columnToggle.toggleColumnsLabel}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={showAllColumns}
              onCheckedChange={toggleShowAllColumns}
            >
              {t.allRuns.columnToggle.showAllColumns}
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={columnVisibility.runId}
              onCheckedChange={() => { toggleColumn('runId'); }}
            >
              {t.allRuns.table.runId}
            </DropdownMenuCheckboxItem>
            {showProfileColumn && (
              <DropdownMenuCheckboxItem
                checked={columnVisibility.profileName}
                onCheckedChange={() => { toggleColumn('profileName'); }}
              >
                {t.allRuns.table.profile}
              </DropdownMenuCheckboxItem>
            )}
            <DropdownMenuCheckboxItem
              checked={columnVisibility.region}
              onCheckedChange={() => { toggleColumn('region'); }}
            >
              {t.allRuns.table.region}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.sourceTableName}
              onCheckedChange={() => { toggleColumn('sourceTableName'); }}
            >
              {t.allRuns.table.sourceTable}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.syncMode}
              onCheckedChange={() => { toggleColumn('syncMode'); }}
            >
              {t.allRuns.table.syncMode}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.state}
              onCheckedChange={() => { toggleColumn('state'); }}
            >
              {t.allRuns.table.state}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.triggerSource}
              onCheckedChange={() => { toggleColumn('triggerSource'); }}
            >
              {t.allRuns.table.triggerSource}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.scheduledUtc}
              onCheckedChange={() => { toggleColumn('scheduledUtc'); }}
            >
              {t.allRuns.table.scheduled}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.startedUtc}
              onCheckedChange={() => { toggleColumn('startedUtc'); }}
            >
              {t.allRuns.table.started}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.completedUtc}
              onCheckedChange={() => { toggleColumn('completedUtc'); }}
            >
              {t.allRuns.table.completed}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.duration}
              onCheckedChange={() => { toggleColumn('duration'); }}
            >
              {t.allRuns.table.duration}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.schemaChanges}
              onCheckedChange={() => { toggleColumn('schemaChanges'); }}
            >
              {t.allRuns.table.schemaChanges}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.actions}
              onCheckedChange={() => { toggleColumn('actions'); }}
            >
              {t.allRuns.table.actions}
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t.allRuns.columnToggle.extractionSection}</DropdownMenuLabel>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.extractionStartedUtc}
              onCheckedChange={() => { toggleColumn('extractionStartedUtc'); }}
            >
              {t.allRuns.table.started}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.extractionCompletedUtc}
              onCheckedChange={() => { toggleColumn('extractionCompletedUtc'); }}
            >
              {t.allRuns.table.completed}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.extractionDuration}
              onCheckedChange={() => { toggleColumn('extractionDuration'); }}
            >
              {t.allRuns.table.duration}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.rowsExtracted}
              onCheckedChange={() => { toggleColumn('rowsExtracted'); }}
            >
              {t.allRuns.columnToggle.rows}
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t.allRuns.columnToggle.loadSection}</DropdownMenuLabel>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.loadStartedUtc}
              onCheckedChange={() => { toggleColumn('loadStartedUtc'); }}
            >
              {t.allRuns.table.started}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.loadCompletedUtc}
              onCheckedChange={() => { toggleColumn('loadCompletedUtc'); }}
            >
              {t.allRuns.table.completed}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.loadDuration}
              onCheckedChange={() => { toggleColumn('loadDuration'); }}
            >
              {t.allRuns.table.duration}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.rowsLoaded}
              onCheckedChange={() => { toggleColumn('rowsLoaded'); }}
            >
              {t.allRuns.columnToggle.rows}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.rowsInserted}
              onCheckedChange={() => { toggleColumn('rowsInserted'); }}
            >
              {t.allRuns.table.rowsInserted}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.rowsUpdated}
              onCheckedChange={() => { toggleColumn('rowsUpdated'); }}
            >
              {t.allRuns.table.rowsUpdated}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={columnVisibility.rowsDeleted}
              onCheckedChange={() => { toggleColumn('rowsDeleted'); }}
            >
              {t.allRuns.table.rowsDeleted}
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Live Update Toggle */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                onClick={() => { setIsLiveUpdateEnabled(!isLiveUpdateEnabled); }}
                className="flex-shrink-0 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring border border-input bg-background shadow-sm h-9 px-4 py-2 hover:bg-accent hover:text-accent-foreground cursor-pointer"
              >
                <Switch
                  id="liveUpdates"
                  checked={isLiveUpdateEnabled}
                  className="pointer-events-none"
                />
                <span className="ml-2">{t.allRuns.liveUpdates.label}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t.allRuns.liveUpdates.tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Show Skipped Runs Toggle */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                onClick={() => { setIsShowSkippedEnabled(!isShowSkippedEnabled); }}
                className="flex-shrink-0 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring border border-input bg-background shadow-sm h-9 px-4 py-2 hover:bg-accent hover:text-accent-foreground cursor-pointer"
              >
                <Switch
                  id="showSkipped"
                  checked={isShowSkippedEnabled}
                  className="pointer-events-none"
                />
                <span className="ml-2">{t.allRuns.showSkipped.label}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t.allRuns.showSkipped.tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Refresh Button */}
        <RefreshButton
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          isLoading={isRefreshing}
          className="flex-shrink-0"
        >
          {t.allRuns.refresh}
        </RefreshButton>
      </div>

      {/* Results */}
      <Card className={fillHeight ? 'flex flex-1 flex-col min-h-0 overflow-hidden' : undefined}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{title ?? t.allRuns.runHistory}</CardTitle>
              <CardDescription>
                {description ?? (filters.length > 0
                  ? `${t.allRuns.filteredResults} (${String(filters.length)} ${filters.length > 1 ? t.allRuns.filtersActive : t.allRuns.filter})`
                  : t.allRuns.allSyncRuns)}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className={fillHeight ? 'p-0 flex flex-1 flex-col min-h-0' : 'p-0'}>
          {isPending ? (
            <>
              <Table className={tableClassName} containerClassName={tableContainerClassName}>
                <TableHeader>
                  <TableRow className="border-t">
                    {columnVisibility.runId && <TableHead>{t.allRuns.table.runId}</TableHead>}
                    {showProfileColumn && columnVisibility.profileName && <TableHead>{t.allRuns.table.profile}</TableHead>}
                    {columnVisibility.region && <TableHead>{t.allRuns.table.region}</TableHead>}
                    {columnVisibility.sourceTableName && <TableHead>{t.allRuns.table.sourceTable}</TableHead>}
                    {columnVisibility.syncMode && <TableHead>{t.allRuns.table.syncMode}</TableHead>}
                    {columnVisibility.state && <TableHead>{t.allRuns.table.state}</TableHead>}
                    {columnVisibility.triggerSource && <TableHead>{t.allRuns.table.triggerSource}</TableHead>}
                    {columnVisibility.scheduledUtc && <TableHead>{t.allRuns.table.scheduled}</TableHead>}
                    {columnVisibility.startedUtc && <TableHead>{t.allRuns.table.started}</TableHead>}
                    {columnVisibility.completedUtc && <TableHead>{t.allRuns.table.completed}</TableHead>}
                    {columnVisibility.duration && <TableHead>{t.allRuns.table.duration}</TableHead>}
                    {columnVisibility.extractionStartedUtc && <TableHead>{t.allRuns.table.extractionStarted}</TableHead>}
                    {columnVisibility.extractionCompletedUtc && <TableHead>{t.allRuns.table.extractionCompleted}</TableHead>}
                    {columnVisibility.extractionDuration && <TableHead>{t.allRuns.table.extractionDuration}</TableHead>}
                    {columnVisibility.loadStartedUtc && <TableHead>{t.allRuns.table.loadStarted}</TableHead>}
                    {columnVisibility.loadCompletedUtc && <TableHead>{t.allRuns.table.loadCompleted}</TableHead>}
                    {columnVisibility.loadDuration && <TableHead>{t.allRuns.table.loadDuration}</TableHead>}
                    {columnVisibility.rowsExtracted && <TableHead>{t.allRuns.table.rowsExtracted}</TableHead>}
                    {columnVisibility.rowsLoaded && <TableHead>{t.allRuns.table.rowsLoaded}</TableHead>}
                    {columnVisibility.rowsInserted && <TableHead>{t.allRuns.table.rowsInserted}</TableHead>}
                    {columnVisibility.rowsUpdated && <TableHead>{t.allRuns.table.rowsUpdated}</TableHead>}
                    {columnVisibility.rowsDeleted && <TableHead>{t.allRuns.table.rowsDeleted}</TableHead>}
                    {columnVisibility.schemaChanges && <TableHead>{t.allRuns.table.schemaChanges}</TableHead>}
                    {columnVisibility.actions && <TableHead>{t.allRuns.table.actions}</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...Array<undefined>(5)].map((_, index) => (
                    <TableRow key={index}>
                      {columnVisibility.runId && (
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      )}
                      {showProfileColumn && columnVisibility.profileName && (
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      )}
                      {columnVisibility.region && (
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      )}
                      {columnVisibility.sourceTableName && (
                        <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                      )}
                      {columnVisibility.syncMode && (
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      )}
                      {columnVisibility.state && (
                        <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                      )}
                      {columnVisibility.triggerSource && (
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      )}
                      {columnVisibility.scheduledUtc && (
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      )}
                      {columnVisibility.startedUtc && (
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      )}
                      {columnVisibility.completedUtc && (
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      )}
                      {columnVisibility.duration && (
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      )}
                      {columnVisibility.extractionStartedUtc && (
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      )}
                      {columnVisibility.extractionCompletedUtc && (
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      )}
                      {columnVisibility.extractionDuration && (
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      )}
                      {columnVisibility.loadStartedUtc && (
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      )}
                      {columnVisibility.loadCompletedUtc && (
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      )}
                      {columnVisibility.loadDuration && (
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      )}
                      {columnVisibility.rowsExtracted && (
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      )}
                      {columnVisibility.rowsLoaded && (
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      )}
                      {columnVisibility.rowsInserted && (
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      )}
                      {columnVisibility.rowsUpdated && (
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      )}
                      {columnVisibility.rowsDeleted && (
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      )}
                      {columnVisibility.schemaChanges && (
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      )}
                      {columnVisibility.actions && (
                        <TableCell><Skeleton className="h-8 w-16" /></TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className={footerClassName}>
                <Skeleton className="h-4 w-40" />
                <div className="flex items-center gap-2">
                  <Skeleton className="h-9 w-24" />
                  <Skeleton className="h-9 w-20" />
                </div>
              </div>
            </>
          ) : runs.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <p>{t.allRuns.noRunsFound}</p>
            </div>
          ) : (
            <>
              <Table className={tableClassName} containerClassName={tableContainerClassName}>
                <TableHeader>
                  <TableRow className="border-t">
                    {columnVisibility.runId && (
                      <TableHead>{t.allRuns.table.runId}</TableHead>
                    )}
                    {showProfileColumn && columnVisibility.profileName && (
                      <TableHead>{t.allRuns.table.profile}</TableHead>
                    )}
                    {columnVisibility.region && (
                      <TableHead>{t.allRuns.table.region}</TableHead>
                    )}
                    {columnVisibility.sourceTableName && (
                      <TableHead>{t.allRuns.table.sourceTable}</TableHead>
                    )}
                    {columnVisibility.syncMode && (
                      <TableHead>{t.allRuns.table.syncMode}</TableHead>
                    )}
                    {columnVisibility.state && (
                      <TableHead>{t.allRuns.table.state}</TableHead>
                    )}
                    {columnVisibility.triggerSource && (
                      <TableHead>{t.allRuns.table.triggerSource}</TableHead>
                    )}
                    {columnVisibility.scheduledUtc && (
                      <TableHead>{t.allRuns.table.scheduled}</TableHead>
                    )}
                    {columnVisibility.startedUtc && (
                      <TableHead>{t.allRuns.table.started}</TableHead>
                    )}
                    {columnVisibility.completedUtc && (
                      <TableHead>{t.allRuns.table.completed}</TableHead>
                    )}
                    {columnVisibility.duration && (
                      <TableHead>{t.allRuns.table.duration}</TableHead>
                    )}
                    {columnVisibility.extractionStartedUtc && (
                      <TableHead>{t.allRuns.table.extractionStarted}</TableHead>
                    )}
                    {columnVisibility.extractionCompletedUtc && (
                      <TableHead>{t.allRuns.table.extractionCompleted}</TableHead>
                    )}
                    {columnVisibility.extractionDuration && (
                      <TableHead>{t.allRuns.table.extractionDuration}</TableHead>
                    )}
                    {columnVisibility.loadStartedUtc && (
                      <TableHead>{t.allRuns.table.loadStarted}</TableHead>
                    )}
                    {columnVisibility.loadCompletedUtc && (
                      <TableHead>{t.allRuns.table.loadCompleted}</TableHead>
                    )}
                    {columnVisibility.loadDuration && (
                      <TableHead>{t.allRuns.table.loadDuration}</TableHead>
                    )}
                    {columnVisibility.rowsExtracted && (
                      <TableHead>{t.allRuns.table.rowsExtracted}</TableHead>
                    )}
                    {columnVisibility.rowsLoaded && (
                      <TableHead>{t.allRuns.table.rowsLoaded}</TableHead>
                    )}
                    {columnVisibility.rowsInserted && (
                      <TableHead>{t.allRuns.table.rowsInserted}</TableHead>
                    )}
                    {columnVisibility.rowsUpdated && (
                      <TableHead>{t.allRuns.table.rowsUpdated}</TableHead>
                    )}
                    {columnVisibility.rowsDeleted && (
                      <TableHead>{t.allRuns.table.rowsDeleted}</TableHead>
                    )}
                    {columnVisibility.schemaChanges && (
                      <TableHead>{t.allRuns.table.schemaChanges}</TableHead>
                    )}
                    {columnVisibility.actions && (
                      <TableHead>{t.allRuns.table.actions}</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map((run) => {
                    const hasError = run.errorCode && run.state !== 'Skipped';
                    const isSkipped = run.state === 'Skipped';
                    const isClickable = hasError ?? isSkipped;
                    const stateDisplay = getStateDisplay(run.state, t.runs.runStates);

                    return (
                      <TableRow
                        key={run.runId}
                        onClick={() => { handleRowClick(run); }}
                        className={isClickable ? 'cursor-pointer' : ''}
                      >
                        {columnVisibility.runId && (
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="font-mono text-xs">
                                      {run.runId.substring(0, 8)}...
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="font-mono">{run.runId}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={(e) => { handleCopyRunId(run.runId, e); }}
                              >
                                {copiedRunId === run.runId ? (
                                  <Check className="h-3 w-3 text-green-600" />
                                ) : (
                                  <Copy className="h-3 w-3" />
                                )}
                              </Button>
                            </div>
                          </TableCell>
                        )}
                        {showProfileColumn && columnVisibility.profileName && (
                          <TableCell className="font-medium">
                            {profileNameMap[run.profileId] || run.profileId.substring(0, 8) + '...'}
                          </TableCell>
                        )}
                        {columnVisibility.region && (
                          <TableCell>{run.region ?? '-'}</TableCell>
                        )}
                        {columnVisibility.sourceTableName && (
                          <TableCell>{run.sourceTableLogicalName || '-'}</TableCell>
                        )}
                        {columnVisibility.syncMode && (
                          <TableCell>
                            {getSyncModeLabel(run.syncMode, t.runs.syncMode)}
                          </TableCell>
                        )}
                        {columnVisibility.state && (
                          <TableCell>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className={`${stateDisplay.className} ${isClickable ? 'cursor-pointer' : ''}`}
                                    onClick={(e) => {
                                      if (isSkipped) {
                                        e.stopPropagation();
                                        handleShowSkipReason(run);
                                      }
                                    }}
                                  >
                                    {stateDisplay.icon}
                                    {stateDisplay.label}
                                    {run.state === 'Extracting' && run.rowsExtracted != null && run.bytesExtracted != null && (
                                      <span>- {formatNumber(run.rowsExtracted)} {t.allRuns.table.rows} ({formatBytes(run.bytesExtracted)})</span>
                                    )}
                                    {run.state === 'Loading' && run.rowsLoaded != null && (
                                      <span>- {formatNumber(run.rowsLoaded)} {t.allRuns.table.rows}</span>
                                    )}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p>{stateDisplay.tooltip}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                        )}
                        {columnVisibility.triggerSource && (
                          <TableCell>
                            {getTriggerSourceLabel(run.triggerSource, t.runs.triggerSource)}
                          </TableCell>
                        )}
                        {columnVisibility.scheduledUtc && (
                          <TableCell>
                            {run.scheduledUtc ? new Date(run.scheduledUtc).toLocaleString() : '-'}
                          </TableCell>
                        )}
                        {columnVisibility.startedUtc && (
                          <TableCell>
                            {run.startedUtc ? new Date(run.startedUtc).toLocaleString() : '-'}
                          </TableCell>
                        )}
                        {columnVisibility.completedUtc && (
                          <TableCell>
                            {run.completedUtc ? new Date(run.completedUtc).toLocaleString() : '-'}
                          </TableCell>
                        )}
                        {columnVisibility.duration && (
                          <TableCell>{formatDuration(run.startedUtc, run.completedUtc)}</TableCell>
                        )}
                        {columnVisibility.extractionStartedUtc && (
                          <TableCell>
                            {run.extractionStartedUtc ? new Date(run.extractionStartedUtc).toLocaleString() : '-'}
                          </TableCell>
                        )}
                        {columnVisibility.extractionCompletedUtc && (
                          <TableCell>
                            {run.extractionCompletedUtc ? new Date(run.extractionCompletedUtc).toLocaleString() : '-'}
                          </TableCell>
                        )}
                        {columnVisibility.extractionDuration && (
                          <TableCell>{formatDuration(run.extractionStartedUtc, run.extractionCompletedUtc)}</TableCell>
                        )}
                        {columnVisibility.loadStartedUtc && (
                          <TableCell>
                            {run.loadStartedUtc ? new Date(run.loadStartedUtc).toLocaleString() : '-'}
                          </TableCell>
                        )}
                        {columnVisibility.loadCompletedUtc && (
                          <TableCell>
                            {run.loadCompletedUtc ? new Date(run.loadCompletedUtc).toLocaleString() : '-'}
                          </TableCell>
                        )}
                        {columnVisibility.loadDuration && (
                          <TableCell>{formatDuration(run.loadStartedUtc, run.loadCompletedUtc)}</TableCell>
                        )}
                        {columnVisibility.rowsExtracted && (
                          <TableCell>{formatNumber(run.rowsExtracted)}</TableCell>
                        )}
                        {columnVisibility.rowsLoaded && (
                          <TableCell>{formatNumber(run.rowsLoaded)}</TableCell>
                        )}
                        {columnVisibility.rowsInserted && (
                          <TableCell>{formatNumber(run.rowsInserted)}</TableCell>
                        )}
                        {columnVisibility.rowsUpdated && (
                          <TableCell>{formatNumber(run.rowsUpdated)}</TableCell>
                        )}
                        {columnVisibility.rowsDeleted && (
                          <TableCell>{formatNumber(run.rowsDeleted)}</TableCell>
                        )}
                        {columnVisibility.schemaChanges && (
                          <TableCell>
                            {run.schemaChanges ? (
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-xs gap-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSchemaChanges(run);
                                  setIsSchemaChangesDialogOpen(true);
                                }}
                              >
                                {t.allRuns.schemaChanges.viewChanges.replace(
                                  '{0}',
                                  String(
                                    (run.schemaChanges.columnsCreated?.length ?? 0) +
                                    (run.schemaChanges.columnsWidened?.length ?? 0) +
                                    (run.schemaChanges.failedMigrations?.length ?? 0)
                                  )
                                )}
                                <ArrowUpRight className="h-3 w-3" />
                              </Button>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        )}
                        {columnVisibility.actions && (
                          <TableCell>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => { handleCancelRun(run, e); }}
                                    disabled={!canCancelRun(run) || isCancelling}
                                  >
                                    <Ban className="h-4 w-4" />
                                    {t.allRuns.actions.cancel}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t.allRuns.actions.cancel}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination Controls */}
              <div className={footerClassName}>
                <div className="text-sm text-muted-foreground">
                  {t.allRuns.pagination.page} {currentPage + 1} • {t.allRuns.pagination.showing} {runs.length} {t.allRuns.pagination.runs}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 0 || isPending}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    {t.allRuns.pagination.previous}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={!hasMore || isPending}
                  >
                    {t.allRuns.pagination.next}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Error Details Dialog */}
      <ErrorDetailsDialog
        open={isErrorDialogOpen}
        onOpenChange={setIsErrorDialogOpen}
        error={selectedError}
        orgId={orgId}
        onResetComplete={() => { void refetch(); }}
      />

      {/* Skip Reason Dialog */}
      <SkipReasonDialog
        open={isSkipReasonDialogOpen}
        onOpenChange={setIsSkipReasonDialogOpen}
        run={selectedSkipReason}
      />

      {/* Schema Changes Dialog */}
      <SchemaChangesDialog
        open={isSchemaChangesDialogOpen}
        onOpenChange={setIsSchemaChangesDialogOpen}
        run={selectedSchemaChanges}
      />
    </>
  );
}
